import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps, Row, Col, Input,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';

const { TextArea } = Input;

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Pages {
  Page_id: number;
  page_slug: string;
  page_title: string;
  page_tagline: string;
  page_short_description: string;
  page_description: string;
  Page_status: number;
}

const Pages: React.FC = () => {
  const [pages, setPages] = useState<Pages[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Pages | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const { slug } = useParams<{ slug: string }>();


  const [editorContent, setEditorContent] = useState('<p>This is the default description</p>');


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // const fetchPage = async (slug: string) => {
  //   try {
  //     const data = await apiRequest<Pages[]>({
  //       method: 'get',
  //       url: `page/${slug}`,
  //     });
  //     setPages(data);
  //   } catch (error) {
  //     showError('Failed to fetch pages');
  //     console.error(error);
  //   }
  // };

  const fetchPage = async (slug: string) => {
    try {
      const data = await apiRequest<Pages>({
        method: 'get',
        url: `page/${slug}`,
      });

      setPages([data]); // Optional - only if you're using the data elsewhere
      setInitialValues(data); // For edit tracking

      form.setFieldsValue({
        page_title: data.page_title,
        page_slug: data.page_slug,
        page_tagline: data.page_tagline,
        page_description: data.page_description,
        page_short_description: data.page_short_description

        // add other fields here if needed
      });

      setIsEditMode(true); // Mark we're in edit mode
    } catch (error) {
      showError('Failed to fetch page');
      console.error(error);
    }
  };


  useEffect(() => {
    fetchPage('about-us');
  }, []);

  const handleEditClick = (record: Pages) => {
    setIsEditMode(true);
    setInitialValues({
      Page_id: record.Page_id,
      page_slug: record.page_slug,
      Page_status: record.Page_status,
      page_title: record.page_title,
      page_tagline: record.page_tagline,
      page_description: record.page_description,
      page_short_description: record.page_short_description,

    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchPage('about-us');
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (Page_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-page/${Page_id}`,
      });
      showSuccess('Page deleted');
      fetchPage('about-us');
    } catch {
      showError('Failed to delete Page');
    }
  };

  const handleStatusToggle = async (pageId: number) => {
    setPages((prev) =>
      prev.map((item) =>
        item.Page_id === pageId
          ? { ...item, Page_status: item.Page_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-page/${pageId}`,
      });
      showSuccess('Page status updated');
    } catch (error) {
      showError('Failed to update status');
      setPages((prev) =>
        prev.map((item) =>
          item.Page_id === pageId
            ? { ...item, Page_status: item.Page_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };



  const handleEditPage = async (values: any) => {
    if (!initialValues?.page_slug) {
      showError('Page slug not found for editing.');
      return;
    }

    try {
      const dataVal = {
        page_slug: initialValues.page_slug, // Keep slug from initialValues
        page_title: values.page_title,
        page_tagline: values.page_tagline,
        page_short_description: values.page_short_description,
        page_description: values.page_description,
      };

      await apiRequest({
        method: 'put',
        url: `edit-page`,
        data: dataVal,
      });

      showSuccess('Page updated successfully!');
      form.resetFields();
      setIsEditMode(false);
      setIsModalVisible(false);
      fetchPage('about-us'); // Optional: refresh data
    } catch (error) {
      console.error(error);
      showError('Failed to update page');
    }
  };



  const onFinish = async (values: any) => {
    if (isEditMode) {
      await handleEditPage(values); // Now passes updated values
    } else {
      showError('Add page functionality not implemented.');
    }
  };



  // On mount, set initial form values
  useEffect(() => {
    form.setFieldsValue({
      page_description: editorContent,
    });
  }, [form, editorContent]);



  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Pages</Breadcrumb.Item>
        </Breadcrumb>


      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >


        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="page_title"
                className='px-8'
                label={
                  <span>
                    Page Title&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      (Max 255 characters)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the Page Title' },
                  { max: 255, message: 'Maximum 255 characters allowed' },
                ]}
              >
                <Input
                  showCount
                  maxLength={255}
                  placeholder="e.g., Page title"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="page_tagline"
                className='px-8'
                label={
                  <span>
                    Tagline&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      (Max 255 characters)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the page Tagline' },
                  { max: 255, message: 'Maximum 255 characters allowed' },
                ]}
              >
                <Input
                  showCount
                  maxLength={255}
                  placeholder="e.g., Page"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="page_short_description"
                className='px-8'
                label={
                  <span>
                    Page Short Description&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      (Max 255 characters)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the Page Short Description' },
                  { max: 255, message: 'Maximum 255 characters allowed' },
                ]}
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>


          <Row gutter={16}>
            <Col span={24}>
              {/* <Form.Item
                name="page_description"
                label="Page Description"
                rules={[{ required: true, message: 'Please enter the page description' }]}
              >
                <Editor
                  apiKey="" // optional: disables the setup screen
                  value={form.getFieldValue('page_description')} // ✅ bind to form
                  onEditorChange={(content) => {
                    form.setFieldsValue({ page_description: content });
                  }}
                  init={{
                    height: 300,
                    menubar: true,
                    plugins: [
                      'advlist autolink lists link image charmap preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount',
                    ],
                    toolbar:
                      'undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | removeformat',
                  }}
                />
              </Form.Item> */}
              <Form.Item
                name="page_description"
                className='px-8'
                label={
                  <span>
                    Page Description&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the Page Description' }
                ]}
              >
                <TextArea rows={4} />
              </Form.Item>



            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space>
                {!isEditMode && (
                  <Button type="primary" htmlType="submit">
                    Submit & Create New Page
                  </Button>
                )}
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

      </div>

    </>
  );
};

export default Pages;